import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { ChevronsUpDown, Check, Building2, Loader2, ShieldCheck, LogOut } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Sidebar selector for platform admins to switch the "active company"
 * context. Only renders when the current user has the admin role.
 */
export default function CompanySwitcher() {
  const { user, switchCompany, switchToPlatform, activeCompanyId, isPlatformAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin || !open || companies.length > 0) return;
    setLoading(true);
    axios
      .get(`${API_URL}/companies`)
      .then((res) => setCompanies(res.data))
      .catch(() => toast.error('No se pudieron cargar las empresas'))
      .finally(() => setLoading(false));
  }, [isAdmin, open, companies.length]);

  if (!isAdmin) return null;

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const onPlatform = !activeCompanyId;

  const handleSelect = async (companyId) => {
    if (companyId === activeCompanyId) return;
    setSwitching(true);
    try {
      const updated = await switchCompany(companyId);
      const c = companies.find((co) => co.id === companyId);
      toast.success(`Trabajando como ${c?.name || updated.company?.name || 'empresa seleccionada'}`);
      setOpen(false);
      setTimeout(() => window.location.reload(), 300);
    } catch (e) {
      toast.error('No se pudo cambiar de empresa');
    } finally {
      setSwitching(false);
    }
  };

  const handleBackToPlatform = async () => {
    if (onPlatform) return;
    setSwitching(true);
    try {
      await switchToPlatform();
      toast.success('Vista plataforma activada');
      setOpen(false);
      setTimeout(() => window.location.reload(), 300);
    } catch (e) {
      toast.error('No se pudo volver a vista plataforma');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-between text-left h-auto py-2 ${!onPlatform ? 'border-amber-300 bg-amber-50/40' : ''}`}
          data-testid="company-switcher"
          disabled={switching}
        >
          <div className="flex items-center gap-2 min-w-0">
            {onPlatform ? (
              <ShieldCheck className="w-4 h-4 flex-shrink-0 text-[#7FD02E]" />
            ) : (
              <Building2 className="w-4 h-4 flex-shrink-0 text-amber-600" />
            )}
            <div className="min-w-0">
              <p className="text-xs text-gray-500 leading-tight">
                {onPlatform ? 'Vista' : 'Trabajando como'}
              </p>
              <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                {onPlatform ? 'Plataforma global' : (activeCompany?.name || 'Empresa')}
              </p>
            </div>
          </div>
          {switching ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          ) : (
            <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-white">
        <DropdownMenuLabel className="text-xs text-gray-500">
          Contexto activo
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleBackToPlatform(); }}
          className="cursor-pointer flex items-center justify-between"
        >
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-4 h-4 text-[#7FD02E]" />
            <div>
              <span className="text-sm font-medium">Plataforma global</span>
              <p className="text-xs text-gray-400">Stats de todas las empresas</p>
            </div>
          </div>
          {onPlatform && <Check className="w-4 h-4 text-[#7FD02E] flex-shrink-0" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">
          Entrar a una empresa
        </DropdownMenuLabel>
        {loading ? (
          <div className="p-4 flex justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        ) : companies.length === 0 ? (
          <div className="px-3 py-4 text-xs text-gray-400 text-center">
            No hay empresas registradas
          </div>
        ) : (
          companies.map((c) => {
            const isActive = c.id === activeCompanyId;
            return (
              <DropdownMenuItem
                key={c.id}
                onSelect={(e) => { e.preventDefault(); handleSelect(c.id); }}
                className="cursor-pointer flex items-center justify-between"
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium truncate">{c.name}</span>
                  <span className="text-xs text-gray-400 truncate">
                    {c.slug}{!c.is_active && ' · inactiva'}
                  </span>
                </div>
                {isActive && <Check className="w-4 h-4 text-[#7FD02E] flex-shrink-0" />}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
